import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Formik, FieldArray } from 'formik';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';

import { IImages } from 'types/auth';
import CommonNodeSetting from './otherComponent/CommonNodeSetting/CommonNodeSetting';
import StepModify from './otherComponent/StepModify';

type ImagesFormProps = {
  selectedNode: any;
  selectedNodeData: IImages;
  onSubmit: (data: IImages) => void;
  onClose: () => void;
};
type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

interface NotificationPayload {
  conditions: string[];
  users: string[];
  roles: string[];
  channels: string[];
}

const ImagesForm = ({ selectedNodeData, onSubmit, onClose, selectedNode }: ImagesFormProps) => {
  const theme = useTheme();
  const [initialValues, setInitialValues] = useState<IImages | null | any>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: [],
  });
  const [escalationPayload, setEscalationPayload] = useState({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] }
  });

  // Memoized callbacks to prevent unnecessary re-renders
  const handleStatusesLoaded = useCallback((list: StatusItem[]) => {
    setStatuses(list);
  }, []);

  const handleNotificationChange = useCallback((payload: NotificationPayload) => {
    console.log('🔔 ImageForm: Notification changed:', payload);
    setNotificationPayload(payload);
  }, []);

  const handleEscalationChange = useCallback((payload: any) => {
    console.log('📞 ImageForm: Escalation changed:', payload);
    setEscalationPayload(payload);
  }, []);

  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, submitForm: any) => {
    if (!selectedNode) return;
    setFieldValue('stepType', newType);
    setFieldValue('componentType', newType);
    setFieldValue('data.title', newType);
     
    setTimeout(() => {
      submitForm();
    });  
  }, [selectedNode]);

  // Memoize notification initial values to prevent Notification component re-renders
  const notificationInitialValues = useMemo(() => {
    // Extract notification data from backend
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
        channel === 'IN-APP' ? 'In-App' : 
        channel === 'EMAIL' ? 'Email' :
        channel === 'SMS' ? 'SMS' : channel
      ) : []
    };
  }, [selectedNode?.data?.step?.notification]);

  useEffect(() => {
    if (selectedNodeData) {
      let { settings, fields, title, component, componentType, onComplete } = selectedNodeData;
      const actor = selectedNode?.data?.step?.actor?.map((a: any) => a?.uid) ?? [];

      // Extract and map notification data from backend
      const backendNotification = selectedNode?.data?.step?.notification || {};
      console.log('🔍 ImageForm: Backend notification data:', backendNotification);
      
      const mappedNotification = {
        scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
        onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
        notifyRoles: backendNotification.roles || [],
        notifyUsers: backendNotification.users || [],
        notificationChannel: backendNotification.channels ? backendNotification.channels.map((channel: string) => 
          channel === 'IN-APP' ? 'In-App' : 
          channel === 'EMAIL' ? 'Email' :
          channel === 'SMS' ? 'SMS' : channel
        ) : []
      };

      console.log('🔍 ImageForm: Mapped notification:', mappedNotification);

      const processedFields = Array.isArray(fields) ? fields.map(field => ({
        ...field,
        inputComponent: field.inputComponent || 'CAMERA'
      })) : [];
      
      console.log('🔥 ImageForm: Original fields:', fields);
      console.log('🔥 ImageForm: Processed fields:', processedFields);

      setInitialValues({
        fields: processedFields,
        settings: {
          maxSize: settings?.maxSize ?? 0,
          width: settings?.width,
          height: settings?.height,
          minWidth: settings?.minWidth,
          minHeight: settings?.minHeight,
          maxWidth: settings?.maxWidth,
          maxHeight: settings?.maxHeight,
          mimeType: settings?.mimeType ?? [],
          watermark: settings?.watermark ?? false,
          coordinate: settings?.coordinate ?? false,
          cameraMode: settings?.cameraMode ?? undefined
        },
        title,
        component,
        componentType,
        name: component?.name ?? 'IMAGE',
        data: {
          title: selectedNode?.data?.label
        },
        actor: actor,
        stepType: selectedNode?.step?.stepType || 'IMAGE',
        onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
        onComplete: onComplete || {}
      });

      // Set notification payload separately to avoid type issues
      setNotificationPayload({
        conditions: [
          ...(mappedNotification.scheduleOnETA ? ['ON_ETA'] : []),
          ...(mappedNotification.onCompletion ? ['ON_COMPLETE'] : [])
        ],
        users: mappedNotification.notifyUsers || [],
        roles: mappedNotification.notifyRoles || [],
        channels: mappedNotification.notificationChannel && mappedNotification.notificationChannel.length > 0 
          ? mappedNotification.notificationChannel.map((channel: string) => 
              channel === 'In-App' ? 'IN-APP' : 
              channel === 'Email' ? 'EMAIL' :
              channel === 'SMS' ? 'SMS' : channel.toUpperCase()
            )
          : []
      });

      // Initialize escalation payload from existing node data
      setEscalationPayload(selectedNode?.data?.step?.escalation || {});
      // Pass the stored object straight through. Each form used to rebuild
      // this by hand from a fixed list of keys, which silently dropped any
      // field it did not know about — including `inherit`, so opening and
      // saving any step would reset it to the process default.

      console.log('🔍 ImageForm: Set notification payload with users:', mappedNotification.notifyUsers, 'roles:', mappedNotification.notifyRoles);
    }
  }, [selectedNodeData, selectedNode]);

  return (
    initialValues && (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          const selectedStatus = statuses.find(s => s.uid === values.onCompleteStatusUid);
          
          const updatedData = {
            ...values,
            component: {
              ...values.component,
              name: values.name || 'IMAGE',
              componentType: values.componentType || 'IMAGE'
            },
            onComplete: selectedStatus ? { status: selectedStatus } : {}
          };

          // Add notification and escalation to the payload (even if not in IImages type)
          const finalData = {
            ...updatedData,
            notification: notificationPayload,
            escalation: escalationPayload
          };

          console.log('🚀 ImageForm: Submitting with notification payload:', notificationPayload);
          console.log('🚀 ImageForm: Final fields data:', finalData.fields);

          onSubmit(finalData as IImages);
          setSubmitting(false);
        }}
      >
        {({ values, handleChange, handleSubmit, setFieldValue, submitForm }) => (
          <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, mx: 'auto' }}>
            {/* Hidden field to ensure name is passed */}
            <input type="hidden" name="name" value={values.name || 'Image'} />

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              {isEditingTitle ? (
                <>
                  <TextField name="data.title" value={values.data.title} onChange={handleChange} autoFocus fullWidth />
                  <Button variant="contained" size="small" sx={{ ml: 2 }} onClick={() => setIsEditingTitle(false)}>
                    Save
                  </Button>
                </>
              ) : (
                <>
                  {/* Left: Title */}
                  <Typography variant="h5" fontWeight="bold">
                    {values.data.title}
                  </Typography>

                  <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={() => setIsEditingTitle(true)}>
                      Edit
                    </Button>
                    {selectedNode && (
                      <Box sx={{ minWidth: '120px', minHeight: '36px' }}>
                    <StepModify
                      selectedNode={selectedNode}
                      onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, submitForm)}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>

            {/* --- Dynamic Image Fields with Accordion --- */}
            <FieldArray name="fields">
              {(arrayHelpers) => (
                <Box mb={3}>
                  {values.fields && values.fields.length > 0 ? (
                    values.fields.map((fields: any, index: number) => (
                      <Accordion
                        key={`fields-${index}`}
                        disableGutters
                        elevation={0}
                        square
                        sx={{
                          mb: 2,
                          border: '1px solid #ccc',
                          borderRadius: 2,
                          overflow: 'hidden',
                          '&:before': {
                            display: 'none'
                          }
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover
                            },
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            '&.Mui-expanded': {
                              minHeight: '48px'
                            },
                            '& .MuiAccordionSummary-content.Mui-expanded': {
                              margin: 0
                            },
                            '& .MuiAccordionSummary-content': {
                              marginRight: 'auto'
                            },
                            '& .MuiAccordionSummary-expandIconWrapper': {
                              color: theme.palette.primary.main
                            }
                          }}
                        >
                          <Typography fontWeight="bold">Image {index + 1}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 3, py: 2 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography>Title</Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <TextField
                                fullWidth
                                size="small"
                                name={`fields.${index}.question`}
                                value={fields.question}
                                onChange={handleChange}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <Typography>Description</Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <TextField
                                fullWidth
                                size="small"
                                name={`fields.${index}.description`}
                                value={fields.description}
                                onChange={handleChange}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Box display="flex" justifyContent="flex-end">
                                <IconButton onClick={() => arrayHelpers.remove(index)} color="error" disabled={values.fields.length === 1}>
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  ) : (
                    <Typography>No images available</Typography>
                  )}
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button 
                      variant="outlined" 
                      startIcon={<AddIcon />} 
                      onClick={() => {
                        const newField = { question: '', description: '', inputComponent: 'Camera' };
                        console.log('🔥 ImageForm: Adding new field:', newField);
                        arrayHelpers.push(newField);
                      }} 
                      sx={{'&:hover': { backgroundColor: theme.palette.action.hover }}}
                    >
                      Add Image
                    </Button>
                  </Box>
                </Box>
              )}
            </FieldArray>

            {/* --- Settings Section --- */}
            <Divider sx={{ my: 3 }} />

            <Accordion sx={{ mt: 4 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography fontWeight='bold'>Fields  Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box border={1} borderColor="grey.300" borderRadius={2} p={2}>
                  <Grid container spacing={2}>
                    {[
                      ['maxSize', 'Max Size'],
                      ['width', 'Width'],
                      ['height', 'Height'],
                      ['minWidth', 'Min Width'],
                      ['minHeight', 'Min Height'],
                      ['maxWidth', 'Max Width'],
                      ['maxHeight', 'Max Height']
                    ].map(([name, label]) => (
                      <React.Fragment key={name}>
                        <Grid item xs={4} display="flex" alignItems="center">
                          <Typography>{label}</Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            name={`settings.${name}`}
                            value={values.settings[name as keyof typeof values.settings] || ''}
                            onChange={handleChange}
                          />
                        </Grid>
                      </React.Fragment>
                    ))}

                    <Grid item xs={4} display="flex" alignItems="center">
                      <Typography>Mime Types</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        size="small"
                        name="settings.mimeType"
                        placeholder="e.g., image/png, image/jpeg"
                        value={(values.settings.mimeType || []).join(', ')}
                        onChange={(e) =>
                          setFieldValue(
                            'settings.mimeType',
                            e.target.value.split(',').map((type) => type.trim())
                          )
                        }
                      />
                    </Grid>

                    <Grid item xs={4} display="flex" alignItems="center">
                      <Typography>Watermark</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControlLabel
                        control={<Checkbox name="settings.watermark" checked={values.settings.watermark} onChange={handleChange} />}
                        label=""
                      />
                    </Grid>

                    <Grid item xs={4} display="flex" alignItems="center">
                      <Typography>Coordinate</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControlLabel
                        control={<Checkbox name="settings.coordinate" checked={values.settings.coordinate} onChange={handleChange} />}
                        label=""
                      />
                    </Grid>

                    <Grid item xs={4} display="flex" alignItems="center">
                      <Typography>Camera Mode</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <FormControl fullWidth>
                        <InputLabel>Camera Mode</InputLabel>
                        <Select
                          size="small"
                          name="settings.cameraMode"
                          value={values.settings.cameraMode ?? ''}
                          onChange={handleChange}
                          label="Camera Mode"
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="environment">Environment</MenuItem>
                          <MenuItem value="front">Front</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </AccordionDetails>
            </Accordion>

            <CommonNodeSetting
              selectedNode={selectedNode}
              onStatusesLoaded={handleStatusesLoaded}
              onNotificationChange={handleNotificationChange}
              notificationInitialValues={notificationInitialValues}
              escalationPayload={escalationPayload}
              onEscalationChange={handleEscalationChange}
            />

            {/* --- Buttons --- */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
              <Button color="error" type="button" onClick={onClose} sx={{'&:hover': { backgroundColor: theme.palette.error.light }}}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" type="submit" sx={{'&:hover': { backgroundColor: theme.palette.primary.dark }}}>
                Save & Close
              </Button>
            </Box>
          </Box>
        )}
      </Formik>
    )
  );
};

export default memo(ImagesForm);
